import React, { useMemo, useEffect, useState, useCallback } from 'react';
import MembersListFilters, { FilterGroup } from '../../../../../../components/MembersListFilters';
import { useFilters } from './useFilters';
import { connect } from 'react-redux';
import { GetAllPostInDto } from '../../../../../../types/post';
import { getUrlParamByName } from '../../../../../../helpers/get-url-param';

interface FiltersProps {
  groups: any[];
  posts: GetAllPostInDto;
  onSubmit: (values: any) => void;
  communitySubscribersStates: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
  autoFiltersApplied?: boolean;
}

const Filters: React.FC<FiltersProps> = ({
  groups,
  posts,
  onSubmit,
  communitySubscribersStates,
  autoFiltersApplied = false
}) => {
  const [formValues, setFormValues] = useState<any>({});

  // Auto-configure filters if lead_magnet_id is present in URL
  useEffect(() => {
    if (!autoFiltersApplied && groups && posts) {
      const leadMagnetId = getUrlParamByName('lead_magnet_id');

      if (leadMagnetId) {
        const leadMagnetIdNumber = parseInt(leadMagnetId, 10);

        if (!isNaN(leadMagnetIdNumber)) {
          // Configure automatic form values
          const autoFormValues = {
            participation_type: 'in',
            group_type: ['lead_magnets_gt'],
            lead_magnet: [leadMagnetIdNumber]
          };

          setFormValues(autoFormValues);

          // Submit the filters automatically
          onSubmit(autoFormValues);
        }
      }
    }
  }, [groups, posts, autoFiltersApplied, onSubmit]);


  const onChange = useCallback((values: any, setFieldValue) => {
    // Si group_type incluye "all_group_types_gt", mantener solo esa opción
    if (values?.group_type && values?.group_type?.includes("all_group_types_gt")) {
      // Si hay más de una opción seleccionada, mantener solo "all_group_types_gt"
      if (values.group_type.length > 1) {
        const updatedValues = { ...values, group_type: ["all_group_types_gt"] };
        setFieldValue('group_type', ["all_group_types_gt"]);
        setFormValues(updatedValues);
      } else {
        setFormValues(values);
      }
    } else {
      // Si no es "all_group_types_gt", resetear campos que ya no son válidos
      const updatedValues = { ...values };
      let hasChanges = false;

      // Mapeo de campos a sus tipos de group_type correspondientes
      const fieldToGroupTypeMap: Record<string, string> = {
        'membership': 'membership_gt',
        'experience': 'experiences_gt',
        'lead_magnet': 'lead_magnets_gt'
      };

      // Verificar cada campo dependiente
      Object.entries(fieldToGroupTypeMap).forEach(([fieldName, requiredGroupType]) => {
        const currentFieldValue = values[fieldName];
        const hasRequiredGroupType = values.group_type && values.group_type.includes(requiredGroupType);

        // Si el campo tiene valor pero group_type ya no incluye el tipo requerido, resetear
        if (currentFieldValue && currentFieldValue.length > 0 && !hasRequiredGroupType) {
          updatedValues[fieldName] = [];
          hasChanges = true;
        }
      });

      setFormValues(hasChanges ? updatedValues : values);
    }
  }, []);

  const onClear = useCallback(() => {
    setFormValues({});
  }, []);

  const getOptions = useCallback((groupType: string) => {
    if (groupType === 'membership') {
      const filteredGroups = groups?.filter((group) => group?.group_type === 'membership');
      return filteredGroups?.map((group) => ({
        label: group.group_name,
        value: group.id
      }))
    }
    if (groupType === 'experience') {
      const filteredGroups = groups?.filter((group) => group?.group_type === 'experience');
      return filteredGroups?.map((group) => ({
        label: group.group_name,
        value: group.id
      }))
    }
    if (groupType === 'lead_magnet') {
      const communityGroup = groups?.find((group) => group?.group_type === 'community');
      const filteredPosts = posts?.data?.filter((post) => post?.group === communityGroup?.id);
      return filteredPosts?.map((post) => ({
        label: post.title,
        value: post.id
      }))
    }
  }, [groups, posts]);

  const membershipFilter = useMemo(() => ({
    title: '',
    filters: [{
      name: 'membership',
      label: 'Membresías',
      type: 'multi' as const,
      options: getOptions("membership") as any,
      placeholder: 'Seleccionar membresías',
      disabled: !formValues?.group_type || formValues?.group_type === '',
    }]
  }), [formValues?.group_type, groups]);

  const experienceFilter = useMemo(() => ({
    title: '',
    filters: [{
      name: 'experience',
      label: 'Talleres',
      type: 'multi' as const,
      options: getOptions("experience") as any,
      placeholder: 'Seleccionar talleres',
      disabled: !formValues?.group_type || formValues?.group_type === '',
    }]
  }), [formValues?.group_type, groups]);

  const leadMagnetFilter = useMemo(() => ({
    title: '',
    filters: [{
      name: 'lead_magnet',
      label: 'Lead Magnets',
      type: 'multi' as const,
      options: getOptions('lead_magnet') as any,
      placeholder: 'Seleccionar lead magnets',
      disabled: !formValues?.group_type || formValues?.group_type === '',
    }]
  }), [formValues?.group_type, posts]);

  // Crear grupos de filtros
  const filtersGroups = useMemo((): FilterGroup[] => [
    {
      title: '',
      filters: [
        {
          name: 'participation_type',
          label: 'Tipo de participación',
          type: 'select' as const,
          options: [
            { value: 'in', label: 'Pertenece a' },
            { value: 'not_in', label: 'No pertenece a' },
          ],
          placeholder: 'Seleccionar tipo de participación',
        },
        {
          name: 'group_type',
          label: 'Tipo de propuesta',
          type: 'multi' as const,
          options: [
            { value: 'all_group_types_gt', label: 'Todos' },
            { value: 'experiences_gt', label: 'Talleres', isDisabled: formValues?.group_type?.includes('all_group_types_gt') },
            { value: 'membership_gt', label: 'Membresías', isDisabled: formValues?.group_type?.includes('all_group_types_gt') },
            { value: 'lead_magnets_gt', label: 'Lead Magnets', isDisabled: formValues?.group_type?.includes('all_group_types_gt') },
          ],
          placeholder: 'Seleccionar tipo de propuesta',
        },
      ],
    },
    ...((formValues?.group_type?.includes('experiences_gt') || formValues?.group_type?.includes('all_group_types_gt')) ? [experienceFilter] : []),
    ...((formValues?.group_type?.includes('membership_gt') || formValues?.group_type?.includes('all_group_types_gt')) ? [membershipFilter] : []),
    ...((formValues?.group_type?.includes('lead_magnets_gt') || formValues?.group_type?.includes('all_group_types_gt')) ? [leadMagnetFilter] : []),
  ], [formValues?.group_type, membershipFilter, experienceFilter, leadMagnetFilter, posts, groups]);

  return (
    <MembersListFilters
      filtersGroup={filtersGroups}
      loading={communitySubscribersStates.loading}
      initialValues={formValues}
      title="Filtros de Comunidad"
      showClearButton={true}
      appliedFilters={formValues}
      onSubmit={onSubmit}
      onClear={onClear}
      onChange={onChange}
    />
  );
};

const state = ({ userStore, postStore, communityStore }) => {
  const { data: groups } = userStore.groups;
  const { data: posts } = postStore.listPosts;
  const { states: communitySubscribersStates } =
    communityStore.subscribers;

  return {
    groups,
    posts,
    communitySubscribersStates
  };
};

export default connect(state)(Filters);
